import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import ETIMClassification from '../components/search/ETIMClassification';
import EngineeringCalculator from '../components/technical/EngineeringCalculator';
import MaterialCompatibility from '../components/technical/MaterialCompatibility';

// Replaced FiCalculator with FiCpu
const { FiBook, FiCpu, FiDroplet, FiGrid, FiDownload } = FiIcons;

const TechnicalDocs = () => {
  const [activeSection, setActiveSection] = useState('etim');

  const sections = {
    etim: {
      title: 'ETIM-Klassifikation',
      icon: FiGrid,
      component: <ETIMClassification onCategorySelect={(code) => console.log('Selected:', code)} />
    },
    calculator: {
      title: 'Ingenieur-Rechner',
      icon: FiCpu,
      component: <EngineeringCalculator />
    },
    materials: {
      title: 'Material-Kompatibilität',
      icon: FiDroplet,
      component: <MaterialCompatibility />
    }
  };

  const technicalDocuments = [
    {
      title: 'Wälzlager-Auslegung nach ISO 281',
      description: 'Berechnung der nominellen Lebensdauer',
      category: 'Wälzlagertechnik',
      format: 'PDF',
      pages: 24,
      language: 'DE/EN'
    },
    {
      title: 'Lineartechnik-Planungshandbuch',
      description: 'Führungssysteme und Antriebskonzepte',
      category: 'Lineartechnik',
      format: 'PDF',
      pages: 156,
      language: 'DE'
    },
    {
      title: 'Dichtungswerkstoffe Kompendium',
      description: 'Materialbeständigkeiten und Einsatzgrenzen',
      category: 'Dichtungstechnik',
      format: 'PDF',
      pages: 89,
      language: 'DE/EN'
    },
    {
      title: 'Riementriebe Auslegungssoftware',
      description: 'Berechnungstool für Keil- und Zahnriemen',
      category: 'Antriebstechnik',
      format: 'EXE',
      pages: '-',
      language: 'DE'
    }
  ];

  return (
    <div className="space-y-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Technische Dokumentation</h1>
            <p className="text-gray-600 mt-2">
              Ingenieur-Tools und Fachunterlagen für die Maschinenelemente-Auslegung
            </p>
          </div>
          <div className="flex items-center space-x-2 bg-red-50 text-red-700 px-4 py-2 rounded-lg border border-red-100">
            <SafeIcon icon={FiBook} className="w-5 h-5" />
            <span className="font-medium">Fachbibliothek</span>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="flex space-x-1 mb-8 bg-gray-100 p-1 rounded-lg">
          {Object.entries(sections).map(([key, section]) => (
            <button
              key={key}
              onClick={() => setActiveSection(key)}
              className={`flex items-center space-x-2 flex-1 px-4 py-3 text-sm font-medium rounded-md transition-colors ${
                activeSection === key
                  ? 'bg-white text-red-600 shadow-sm border border-red-200'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-200'
              }`}
            >
              <SafeIcon icon={section.icon} className="w-4 h-4" />
              <span>{section.title}</span>
            </button>
          ))}
        </div>

        {/* Active Section Content */}
        <div className="mb-8">
          {sections[activeSection].component}
        </div>

        {/* Technical Documents Library */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center space-x-3 mb-6">
            <div className="w-10 h-10 bg-red-600 rounded-lg flex items-center justify-center">
              <SafeIcon icon={FiBook} className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Technische Unterlagen</h2>
              <p className="text-gray-600">Handbücher und Berechnungstools</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {technicalDocuments.map((doc, index) => (
              <motion.div
                key={index}
                className="p-4 border border-gray-200 rounded-lg hover:border-red-200 hover:bg-red-50/30 transition-colors"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
                whileHover={{ scale: 1.02 }}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900 mb-1">{doc.title}</h3>
                    <p className="text-sm text-gray-600 mb-2">{doc.description}</p>
                    <span className="inline-block px-2 py-1 bg-gray-100 text-gray-800 rounded text-xs font-medium">
                      {doc.category}
                    </span>
                  </div>
                </div>
                
                <div className="flex items-center justify-between text-sm text-gray-500">
                  <div className="flex items-center space-x-4">
                    <span>{doc.format}</span>
                    {doc.pages !== '-' && <span>{doc.pages} Seiten</span>}
                    <span>{doc.language}</span>
                  </div>
                  <button className="flex items-center space-x-1 text-red-600 hover:text-red-700 font-medium">
                    <SafeIcon icon={FiDownload} className="w-4 h-4" />
                    <span>Download</span>
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Standards and Norms */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Normen und Standards</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">Wälzlager</h3>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• ISO 281 (Lebensdauer)</li>
                <li>• ISO 492 (Toleranzen)</li>
                <li>• DIN 625 (Rillenkugellager)</li>
                <li>• ISO 15312 (Dichtungen)</li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">Lineartechnik</h3>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• ISO 14728 (Linearführungen)</li>
                <li>• DIN 69051 (T-Nuten)</li>
                <li>• ISO 3408 (Kugelgewindetriebe)</li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">Dichtungstechnik</h3>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• DIN 3760 (Wellendichtringe)</li>
                <li>• ISO 3601 (O-Ringe)</li>
                <li>• DIN 7863 (Hydraulikdichtungen)</li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">Antriebstechnik</h3>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• DIN 7753 (Keilriemen)</li>
                <li>• ISO 5296 (Zahnriemen)</li>
                <li>• DIN 8187 (Rollenketten)</li>
              </ul>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default TechnicalDocs;